import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/lib/property";
import { useAuth, useIsMaster } from "@/lib/auth";
import { usePermission } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import { PlusIcon, PencilSimpleIcon, ClockCountdownIcon, WarningIcon, StackIcon, TrashIcon } from "@phosphor-icons/react";
import { ROOM } from "@/constants/testIds";
import { ChannelChip, CHANNEL_META } from "@/components/ChannelChip";

const TYPES = ["Standard", "Superior", "Deluxe", "Premium", "Suite", "Family"];
const EMPTY = { number: "", room_type: "Deluxe", floor: 1, rate_per_night: 3500, max_occupancy: 2 };

// Compute the checkout Date object for a reservation given the property's checkout time.
function computeCheckoutMoment(res, propertyCheckoutTime) {
  if (!res?.check_out) return null;
  const [h, m] = (propertyCheckoutTime || "11:00").split(":").map((v) => parseInt(v, 10) || 0);
  const [y, mo, d] = res.check_out.split("-").map((v) => parseInt(v, 10));
  return new Date(y, mo - 1, d, h, m, 0, 0);
}

function formatRemaining(ms) {
  const totalMin = Math.floor(ms / 60000);
  if (totalMin <= 0) return "overdue";
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin - days * 1440) / 60);
  const mins = totalMin - days * 1440 - hours * 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function CountdownBadge({ ms }) {
  if (ms === null || ms === undefined) return null;
  const overdue = ms <= 0;
  const soon = !overdue && ms <= 30 * 60 * 1000; // ≤30 min
  const cls = overdue
    ? "bg-destructive/15 text-destructive border-destructive/40"
    : soon
    ? "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/50 animate-pulse"
    : "bg-muted text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono-plex border ${cls}`}>
      <ClockCountdownIcon size={10} />
      {overdue ? "overdue" : `out in ${formatRemaining(ms)}`}
    </span>
  );
}

function RoomForm({ initial, onSubmit, submitLabel, testIdSubmit }) {
  const [form, setForm] = useState(initial);
  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      rate_per_night: Number(form.rate_per_night),
      floor: Number(form.floor),
      max_occupancy: Number(form.max_occupancy),
    });
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Number</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} required data-testid="room-number-input" /></div>
        <div><Label>Floor</Label><Input type="number" value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} /></div>
      </div>
      <div>
        <Label>Type</Label>
        <Select value={form.room_type} onValueChange={(v) => setForm({ ...form, room_type: v })}>
          <SelectTrigger data-testid="room-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Rate / night (₹)</Label>
          <Input type="number" step="0.01" value={form.rate_per_night} onChange={(e) => setForm({ ...form, rate_per_night: e.target.value })} data-testid="room-rate-input" />
          <p className="text-[10px] text-muted-foreground mt-1">Base tariff. GST inclusive/exclusive is chosen per invoice.</p>
        </div>
        <div><Label>Max occupancy</Label><Input type="number" value={form.max_occupancy} onChange={(e) => setForm({ ...form, max_occupancy: e.target.value })} /></div>
      </div>
      <DialogFooter><Button type="submit" data-testid={testIdSubmit}>{submitLabel}</Button></DialogFooter>
    </form>
  );
}

export default function Rooms() {
  const { activeId, active } = useProperty();
  const { user } = useAuth();
  const isMaster = useIsMaster();
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [now, setNow] = useState(Date.now());

  const load = () => activeId && Promise.all([
    api.get("/rooms", { params: { property_id: activeId } }),
    api.get("/reservations", { params: { property_id: activeId } }),
  ]).then(([roomsRes, resRes]) => {
    // Attach the current active reservation (if any) to each occupied room
    const today = new Date().toISOString().slice(0, 10);
    const activeRes = resRes.data.filter((r) => r.status === "checked_in" && r.check_in <= today && r.check_out >= today);
    const byRoom = Object.fromEntries(activeRes.map((r) => [r.room_id, r]));
    setRooms(roomsRes.data.map((r) => ({ ...r, current_reservation: byRoom[r.id] || null })));
  });
  useEffect(() => { load(); }, [activeId]);

  // Refresh "now" every 30s so countdowns tick without hammering.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const checkoutTime = active?.check_out_time || "11:00";

  // Enrich rooms with checkout ms remaining
  const enriched = useMemo(() => rooms.map((r) => {
    const moment = r.current_reservation ? computeCheckoutMoment(r.current_reservation, checkoutTime) : null;
    return { ...r, checkout_moment: moment, ms_left: moment ? moment.getTime() - now : null };
  }), [rooms, checkoutTime, now]);

  const dueSoon = enriched.filter((r) => r.ms_left !== null && r.ms_left <= 30 * 60 * 1000 && r.ms_left > -6 * 60 * 60 * 1000);
  const overdue = dueSoon.filter((r) => r.ms_left <= 0);

  const create = async (payload) => {
    try {
      await api.post("/rooms", { ...payload, property_id: activeId });
      toast.success("Room added");
      setOpen(false);
      load();
    } catch { toast.error("Failed to add room"); }
  };

  const saveEdit = async (payload) => {
    try {
      const { number, room_type, floor, rate_per_night, max_occupancy } = payload;
      await api.patch(`/rooms/${editing.id}`, { number, room_type, floor, rate_per_night, max_occupancy });
      toast.success("Room updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const setStatus = async (room, status) => {
    await api.patch(`/rooms/${room.id}`, { status });
    load();
  };

  const removeRoom = async (room) => {
    if (room.status === "occupied" || room.current_reservation) {
      toast.error(`Room ${room.number} is currently occupied — check out the guest first.`);
      return;
    }
    const confirmText = `Delete room ${room.number} permanently? This cannot be undone.`;
    if (!window.confirm(confirmText)) return;
    try {
      await api.delete(`/rooms/${room.id}`);
      toast.success(`Room ${room.number} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const canManage = usePermission("rooms").canWrite;
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ room_type: "Deluxe", floor: 1, start_number: 1, count: 10, rate_per_night: 3500, max_occupancy: 2, floor_prefix: true });

  const bulkPreview = useMemo(() => {
    const out = [];
    for (let i = 0; i < Math.min(Number(bulk.count) || 0, 6); i++) {
      const base = Number(bulk.start_number) + i;
      out.push(bulk.floor_prefix ? String(Number(bulk.floor) * 100 + base) : String(base));
    }
    const total = Number(bulk.count) || 0;
    if (total > 6) out.push(`+${total - 6}`);
    return out;
  }, [bulk]);

  const submitBulk = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/rooms/bulk", { ...bulk, property_id: activeId, count: Number(bulk.count), floor: Number(bulk.floor), start_number: Number(bulk.start_number), rate_per_night: Number(bulk.rate_per_night), max_occupancy: Number(bulk.max_occupancy) });
      toast.success(`Created ${r.data.created_count} rooms${r.data.skipped.length ? ` · skipped ${r.data.skipped.length} existing` : ""}`);
      setBulkOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Bulk create failed"); }
  };

  return (
    <div>
      <PageHeader
        overline="Inventory"
        title="Rooms"
        description={`Live status and tariff · check-out at ${checkoutTime} (property-local).`}
        actions={canManage && (
          <div className="flex gap-2">
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="rooms-bulk-btn"><StackIcon size={14} className="mr-1" />Bulk add</Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Bulk add rooms</DialogTitle></DialogHeader>
                <form onSubmit={submitBulk} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Room type</Label>
                      <Select value={bulk.room_type} onValueChange={(v) => setBulk({ ...bulk, room_type: v })}>
                        <SelectTrigger data-testid="bulk-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Floor</Label><Input type="number" min="0" max="99" value={bulk.floor} onChange={(e) => setBulk({ ...bulk, floor: e.target.value })} data-testid="bulk-floor" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Start #</Label><Input type="number" min="1" value={bulk.start_number} onChange={(e) => setBulk({ ...bulk, start_number: e.target.value })} data-testid="bulk-start" /></div>
                    <div><Label>How many</Label><Input type="number" min="1" max="200" value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: e.target.value })} data-testid="bulk-count" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Rate/night (₹)</Label><Input type="number" min="0" value={bulk.rate_per_night} onChange={(e) => setBulk({ ...bulk, rate_per_night: e.target.value })} data-testid="bulk-rate" /></div>
                    <div><Label>Max occupancy</Label><Input type="number" min="1" max="10" value={bulk.max_occupancy} onChange={(e) => setBulk({ ...bulk, max_occupancy: e.target.value })} data-testid="bulk-occupancy" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={bulk.floor_prefix} onChange={(e) => setBulk({ ...bulk, floor_prefix: e.target.checked })} data-testid="bulk-floor-prefix" />
                    Auto-prefix numbers with floor (floor 2 + start 1 = 201, 202, …)
                  </label>
                  <div className="rounded-md bg-muted p-3 text-xs" data-testid="bulk-preview">
                    <span className="text-muted-foreground">Will create rooms:</span>{" "}
                    <span className="font-mono-plex">{bulkPreview.join(", ")}</span>
                  </div>
                  <DialogFooter><Button type="submit" data-testid="bulk-submit">Create {bulk.count} rooms</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid={ROOM.newBtn}><PlusIcon size={16} className="mr-1" />Add room</Button></DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>New room</DialogTitle></DialogHeader>
                <RoomForm initial={EMPTY} onSubmit={create} submitLabel="Create" testIdSubmit={ROOM.submitBtn} />
              </DialogContent>
            </Dialog>
          </div>
        )}
      />

      {/* Status banner — always shows so staff know the feature is armed */}
      <Card
        className={`p-3 mb-4 flex flex-wrap gap-2 items-center border ${overdue.length > 0 ? "border-destructive/50 bg-destructive/10" : dueSoon.length > 0 ? "border-[hsl(var(--warning))]/50 bg-[hsl(var(--warning))]/10" : "border-border bg-muted/40"}`}
        data-testid="rooms-checkout-alert"
      >
        <ClockCountdownIcon
          size={16}
          weight="duotone"
          className={overdue.length > 0 ? "text-destructive" : dueSoon.length > 0 ? "text-[hsl(var(--warning))]" : "text-muted-foreground"}
        />
        <div className="flex-1 text-sm">
          {enriched.filter((r) => r.current_reservation).length === 0 ? (
            <span className="text-muted-foreground">
              No rooms currently checked-in for <strong>{active?.name}</strong>. Countdown chips appear on the room card the moment a reservation is checked-in — property check-out set to <strong>{checkoutTime}</strong>.
            </span>
          ) : dueSoon.length === 0 ? (
            <span className="text-muted-foreground">
              <strong>{enriched.filter((r) => r.current_reservation).length}</strong> rooms checked-in · none due out in the next 30 min. Live countdown on each occupied card · property check-out {checkoutTime}.
            </span>
          ) : (
            <>
              <strong>{dueSoon.length}</strong> room{dueSoon.length === 1 ? "" : "s"} due for check-out within 30 minutes
              {overdue.length > 0 && <span className="text-destructive font-medium"> · {overdue.length} overdue</span>}:
              <span className="ml-2 text-muted-foreground">
                {dueSoon.slice(0, 6).map((r) => (
                  <span key={r.id} className="mr-2 inline-flex items-center gap-1">
                    <span className="font-mono-plex">#{r.number}</span>
                    <CountdownBadge ms={r.ms_left} />
                  </span>
                ))}
                {dueSoon.length > 6 && <span>+{dueSoon.length - 6} more</span>}
              </span>
            </>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {enriched.map((r) => (
          <Card key={r.id} className="p-4" data-testid={`room-card-${r.number}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="label-overline">{r.room_type}</p>
                <p className="font-display text-2xl mt-1">{r.number}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                {r.status === "occupied" && <span className="w-2 h-2 rounded-full bg-secondary pulse-dot mt-2" />}
                {r.current_reservation && (
                  <ChannelChip channel={r.current_reservation.channel || "direct"} size={16} />
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">₹{Number(r.rate_per_night).toLocaleString("en-IN")}/night · fits {r.max_occupancy}</p>
            {r.current_reservation && (
              <>
                <p className="text-[10px] text-muted-foreground mt-1 truncate" title={`${r.current_reservation.guest_name} via ${CHANNEL_META[r.current_reservation.channel || "direct"].label}`}>
                  {r.current_reservation.guest_name} · via <span className="font-medium">{CHANNEL_META[r.current_reservation.channel || "direct"].label}</span>
                </p>
                <div className="mt-2" data-testid={`room-countdown-${r.number}`}>
                  <CountdownBadge ms={r.ms_left} />
                </div>
              </>
            )}
            <div className="mt-3"><StatusBadge status={r.status} /></div>
            {canManage && (
              <div className="mt-3 space-y-2">
                <Select value={r.status} onValueChange={(v) => setStatus(r, v)}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="occupied">Occupied</SelectItem>
                    <SelectItem value="cleaning">Cleaning</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 text-xs"
                    onClick={() => setEditing(r)}
                    data-testid={`room-edit-${r.number}`}
                  >
                    <PencilSimpleIcon size={12} className="mr-1" /> Edit
                  </Button>
                  {isMaster && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs text-destructive hover:bg-destructive hover:text-destructive-foreground border-destructive/40"
                      onClick={() => removeRoom(r)}
                      title="Delete room (master owner only)"
                      data-testid={`room-delete-${r.number}`}
                    >
                      <TrashIcon size={12} />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>

      {editing && (
        <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit room {editing.number}</DialogTitle></DialogHeader>
            <RoomForm
              initial={{
                number: editing.number,
                room_type: editing.room_type,
                floor: editing.floor,
                rate_per_night: editing.rate_per_night,
                max_occupancy: editing.max_occupancy,
              }}
              onSubmit={saveEdit}
              submitLabel="Save changes"
              testIdSubmit="room-edit-save-btn"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
